import * as React from 'react';
import {
    Dimensions,
    type LayoutRectangle,
    type StyleProp,
    StyleSheet,
    type Text,
    type View,
    type ViewStyle,
} from 'react-native';

import {
    createDebouncedFlush,
    useEventCallback,
    useSafeLayoutEffect,
} from '@gitbook/util-react-hooks';

import type { LayoutDimensions } from '../Responsive';
import { measureNodeInWindow } from '../util';
import {
    computePopoverPosition,
    type PopoverDimensionsRange,
    type PopoverPositionOptions,
} from './computePopoverPosition';
import type { PopoverAlignment, PopoverPlacement } from './types';

type PopoverPositionInputRef = React.RefObject<View | HTMLElement | Text | undefined>;

/**
 * Compute the best position for a popover dynamically.
 */
export function usePopoverPosition(
    anchorRef: PopoverPositionInputRef | LayoutRectangle | null | undefined,
    popoverRef:
        | PopoverPositionInputRef
        | (LayoutDimensions & Partial<PopoverDimensionsRange>)
        | ((
              anchorDimensions: LayoutRectangle
          ) => LayoutDimensions & Partial<PopoverDimensionsRange>)
        | null
        | undefined,
    options: Partial<PopoverPositionOptions> = {}
): [StyleProp<ViewStyle>, PopoverPlacement, PopoverAlignment, () => void] {
    const [position, setPosition] = React.useState<
        null | [StyleProp<ViewStyle>, PopoverPlacement, PopoverAlignment]
    >(null);

    const updatePosition = useEventCallback(() => {
        if (!anchorRef || !popoverRef) {
            return;
        }

        const resetPosition = () => {
            setPosition(null);
        };

        const anchorRect = measureRefPosition(anchorRef);
        if (!anchorRect) {
            resetPosition();
            return;
        }

        const popoverRect =
            typeof popoverRef === 'function'
                ? popoverRef(anchorRect)
                : measureRefPosition(popoverRef);

        if (!popoverRect) {
            resetPosition();
            return;
        }

        const windowSize = Dimensions.get('window');
        const newPosition = computePopoverPosition(anchorRect, popoverRect, windowSize, options);

        setPosition(newPosition);
    });

    //
    // Update position as soon as we render the component
    // We want updatePosition() to be called before any animations is started by AnimateLifecycle
    //
    useSafeLayoutEffect(() => {
        updatePosition();
    }, [updatePosition]);

    //
    // Update position when resized
    //
    useSafeLayoutEffect(() => {
        const element = popoverRef && 'current' in popoverRef ? popoverRef.current : null;
        if (!(element instanceof HTMLElement)) {
            return;
        }

        const debounce = createDebouncedFlush(300);

        const resizeObserver = new ResizeObserver(() => {
            debounce(() => {
                updatePosition();
            });
        });
        resizeObserver.observe(element);
        return () => {
            resizeObserver.disconnect();
        };
    }, [popoverRef, updatePosition]);

    return [
        ...(position || [
            styles.hidden,
            options.preferredPlacement || 'bottom',
            options.preferredAlignment || 'start',
        ]),
        updatePosition,
    ];
}

/**
 * Measure a ref input's position and return its layout as a rectangle
 */
function measureRefPosition<T extends LayoutRectangle | LayoutDimensions>(
    ref: PopoverPositionInputRef | T
): T | null {
    if ('width' in ref) {
        return ref;
    }
    if (ref.current) {
        return measureNodeInWindow(ref.current) as T;
    }
    return null;
}

const styles = StyleSheet.create({
    hidden: {
        opacity: 0,
        // Don't startt the animation until we have computed the position
        animationPlayState: 'paused',
        // Since the element is mis-positioned during the compute of position, ignore pointer events
        pointerEvents: 'none',
    },
});
